import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface CollectionTemplate {
  id: string;
  name: string;
  category: string;
  active: boolean;
  acorn_reward: number;
  step_count: number;
}

interface Step {
  id: string;
  collection_template_id: string;
  content_library_id: string;
  order_index: number;
  required: boolean;
  repeatable: boolean;
  cooldown_days: number | null;
  step_title_override: string | null;
  card_title: string;
  card_type: string;
}

interface ContentCard {
  id: string;
  title: string | null;
  body: string;
  type: string;
}

interface StepRow {
  id: string;
  collection_template_id: string;
  content_library_id: string;
  order_index: number;
  required: boolean;
  repeatable: boolean;
  cooldown_days: number | null;
  step_title_override: string | null;
  content_library: {
    id: string;
    title: string | null;
    body: string;
    type: string;
  } | null;
}

type View = { kind: "list" } | { kind: "steps"; template: CollectionTemplate };

export default function CollectionTemplateEditor() {
  const [view, setView] = useState<View>({ kind: "list" });
  const [templates, setTemplates] = useState<CollectionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  // Step editor state
  const [steps, setSteps] = useState<Step[]>([]);
  const [stepsLoading, setStepsLoading] = useState(false);
  const [savingStep, setSavingStep] = useState<string | null>(null);
  const [deletingStep, setDeletingStep] = useState<string | null>(null);

  // Add step form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [cardSearch, setCardSearch] = useState("");
  const [cardResults, setCardResults] = useState<ContentCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<ContentCard | null>(null);
  const [addRequired, setAddRequired] = useState(true);
  const [addRepeatable, setAddRepeatable] = useState(false);
  const [addCooldown, setAddCooldown] = useState<string>("");
  const [addTitleOverride, setAddTitleOverride] = useState("");
  const [addingStep, setAddingStep] = useState(false);
  const [cardSearchLoading, setCardSearchLoading] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    setLoading(true);
    const { data: rows, error } = await supabase
      .from("collection_templates")
      .select("id, name, category, active, acorn_reward")
      .order("sort_order");

    if (error) {
      toast.error("Failed to load collection templates");
      setLoading(false);
      return;
    }

    const { data: stepCounts } = await supabase
      .from("collection_template_steps")
      .select("collection_template_id");

    const countMap: Record<string, number> = {};
    for (const row of stepCounts ?? []) {
      countMap[row.collection_template_id] = (countMap[row.collection_template_id] ?? 0) + 1;
    }

    setTemplates(
      (rows ?? []).map((t) => ({ ...t, step_count: countMap[t.id] ?? 0 }))
    );
    setLoading(false);
  }

  async function loadSteps(templateId: string) {
    setStepsLoading(true);
    const { data, error } = await supabase
      .from("collection_template_steps")
      .select(`
        id,
        collection_template_id,
        content_library_id,
        order_index,
        required,
        repeatable,
        cooldown_days,
        step_title_override,
        content_library!inner(id, title, body, type)
      `)
      .eq("collection_template_id", templateId)
      .order("order_index");

    if (error) {
      toast.error("Failed to load steps");
      setStepsLoading(false);
      return;
    }

    setSteps(
      (data as StepRow[] ?? []).map((row) => ({
        id: row.id,
        collection_template_id: row.collection_template_id,
        content_library_id: row.content_library_id,
        order_index: row.order_index,
        required: row.required,
        repeatable: row.repeatable,
        cooldown_days: row.cooldown_days,
        step_title_override: row.step_title_override,
        card_title: row.content_library?.title || row.content_library?.body?.slice(0, 60) || "Untitled",
        card_type: row.content_library?.type || "unknown",
      }))
    );
    setStepsLoading(false);
  }

  function openStepEditor(template: CollectionTemplate) {
    setView({ kind: "steps", template });
    loadSteps(template.id);
    setShowAddForm(false);
    setSelectedCard(null);
    setCardSearch("");
    setCardResults([]);
  }

  function backToList() {
    setView({ kind: "list" });
    setSteps([]);
    setShowAddForm(false);
  }

  function setReward(id: string, value: number) {
    setTemplates((prev) =>
      prev.map((t) => (t.id === id ? { ...t, acorn_reward: value } : t))
    );
  }

  async function saveReward(template: CollectionTemplate) {
    setSaving(template.id);
    const { error } = await supabase
      .from("collection_templates")
      .update({ acorn_reward: template.acorn_reward })
      .eq("id", template.id);
    setSaving(null);
    if (error) {
      toast.error("Failed to save");
    } else {
      toast.success(`${template.name} updated`);
    }
  }

  // -- Step inline edit --

  function updateStepLocal(id: string, patch: Partial<Step>) {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function isStepConflicted(step: Step) {
    return step.required && step.repeatable;
  }

  async function saveStep(step: Step) {
    if (isStepConflicted(step)) return;
    setSavingStep(step.id);
    const { error } = await supabase
      .from("collection_template_steps")
      .update({
        required: step.required,
        repeatable: step.repeatable,
        cooldown_days: step.cooldown_days,
        step_title_override: step.step_title_override || null,
      })
      .eq("id", step.id);
    setSavingStep(null);
    if (error) {
      toast.error(error.message || "Failed to save step");
    } else {
      toast.success("Step saved");
    }
  }

  async function deleteStep(stepId: string) {
    setDeletingStep(stepId);
    const { error } = await supabase
      .from("collection_template_steps")
      .delete()
      .eq("id", stepId);
    setDeletingStep(null);
    if (error) {
      toast.error("Failed to delete step");
    } else {
      setSteps((prev) => prev.filter((s) => s.id !== stepId));
      toast.success("Step removed");
    }
  }

  // Swap order_index between two adjacent rows.
  // UNIQUE(collection_template_id, order_index) requires a temp sentinel to avoid collision.
  async function moveStep(stepId: string, direction: "up" | "down") {
    const idx = steps.findIndex((s) => s.id === stepId);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= steps.length) return;

    const a = steps[idx];
    const b = steps[swapIdx];
    const SENTINEL = -999;

    // Step 1: set a to sentinel
    const { error: e1 } = await supabase
      .from("collection_template_steps")
      .update({ order_index: SENTINEL })
      .eq("id", a.id);
    if (e1) { toast.error("Reorder failed"); return; }

    // Step 2: set b to a's original index
    const { error: e2 } = await supabase
      .from("collection_template_steps")
      .update({ order_index: a.order_index })
      .eq("id", b.id);
    if (e2) { toast.error("Reorder failed"); return; }

    // Step 3: set a from sentinel to b's original index
    const { error: e3 } = await supabase
      .from("collection_template_steps")
      .update({ order_index: b.order_index })
      .eq("id", a.id);
    if (e3) { toast.error("Reorder failed"); return; }

    // Update local state
    setSteps((prev) => {
      const next = [...prev];
      next[idx] = { ...a, order_index: b.order_index };
      next[swapIdx] = { ...b, order_index: a.order_index };
      return next.sort((x, y) => x.order_index - y.order_index);
    });
  }

  // -- Content library picker --

  async function searchCards(q: string) {
    setCardSearch(q);
    if (q.trim().length < 2) {
      setCardResults([]);
      return;
    }
    setCardSearchLoading(true);
    const { data, error } = await supabase
      .from("content_library")
      .select("id, title, body, type")
      .ilike("title", `%${q}%`)
      .eq("active", true)
      .order("title")
      .limit(20);
    setCardSearchLoading(false);
    if (!error) {
      setCardResults(data ?? []);
    }
  }

  async function addStep() {
    if (!selectedCard || view.kind !== "steps") return;
    if (addRequired && addRepeatable) return;
    setAddingStep(true);

    const nextOrder = steps.length > 0
      ? Math.max(...steps.map((s) => s.order_index)) + 1
      : 0;

    const { data, error } = await supabase
      .from("collection_template_steps")
      .insert({
        collection_template_id: view.template.id,
        content_library_id: selectedCard.id,
        order_index: nextOrder,
        required: addRequired,
        repeatable: addRepeatable,
        cooldown_days: addCooldown !== "" ? parseInt(addCooldown) : null,
        step_title_override: addTitleOverride.trim() || null,
      })
      .select(`
        id,
        collection_template_id,
        content_library_id,
        order_index,
        required,
        repeatable,
        cooldown_days,
        step_title_override,
        content_library!inner(id, title, body, type)
      `)
      .single();

    setAddingStep(false);

    if (error) {
      toast.error(error.message || "Failed to add step");
      return;
    }

    const row = data as StepRow;
    setSteps((prev) => [
      ...prev,
      {
        id: row.id,
        collection_template_id: row.collection_template_id,
        content_library_id: row.content_library_id,
        order_index: row.order_index,
        required: row.required,
        repeatable: row.repeatable,
        cooldown_days: row.cooldown_days,
        step_title_override: row.step_title_override,
        card_title: row.content_library?.title || row.content_library?.body?.slice(0, 60) || "Untitled",
        card_type: row.content_library?.type || "unknown",
      },
    ]);

    // Reset form
    setShowAddForm(false);
    setSelectedCard(null);
    setCardSearch("");
    setCardResults([]);
    setAddRequired(true);
    setAddRepeatable(false);
    setAddCooldown("");
    setAddTitleOverride("");
    toast.success("Step added");
  }

  // -- Render --

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-12">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (view.kind === "list") {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">Collection Templates</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {templates.length} templates — manage acorn rewards and steps. Templates are created via migrations.
          </p>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Active</th>
              <th>Steps</th>
              <th>Acorn reward</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id}>
                <td className="font-medium text-gray-900 text-sm">{t.name}</td>
                <td>
                  <span className="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
                    {t.category}
                  </span>
                </td>
                <td className="text-sm">
                  {t.active ? (
                    <span className="text-green-700 font-medium">Yes</span>
                  ) : (
                    <span className="text-gray-400">No</span>
                  )}
                </td>
                <td>
                  <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs font-mono">
                    {t.step_count}
                  </span>
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={t.acorn_reward}
                    onChange={(e) =>
                      setReward(t.id, Math.max(0, parseInt(e.target.value) || 0))
                    }
                    className="admin-input w-20"
                  />
                </td>
                <td>
                  <button
                    onClick={() => saveReward(t)}
                    disabled={saving === t.id}
                    className="rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    {saving === t.id ? "Saving…" : "Save"}
                  </button>
                </td>
                <td>
                  <button
                    onClick={() => openStepEditor(t)}
                    className="rounded-md bg-gray-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 transition-colors"
                  >
                    Edit steps
                  </button>
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-gray-400 py-8">
                  No templates found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  }

  // View 2 — step editor
  const template = view.template;
  const addConflict = addRequired && addRepeatable;

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={backToList}
          className="text-sm text-orange-600 hover:text-orange-700 mb-3 flex items-center gap-1"
        >
          ← Back to templates
        </button>
        <h1 className="text-xl font-bold text-gray-900">{template.name} — Steps</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {steps.length} step{steps.length !== 1 ? "s" : ""}
        </p>
      </div>

      {stepsLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
          Loading steps…
        </div>
      ) : (
        <>
          <table className="admin-table mb-6">
            <thead>
              <tr>
                <th className="w-16">Order</th>
                <th>Card</th>
                <th className="w-24">Required</th>
                <th className="w-28">Repeatable</th>
                <th className="w-32">Cooldown (days)</th>
                <th>Title override</th>
                <th className="w-20"></th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, i) => {
                const conflict = isStepConflicted(step);
                return (
                  <tr key={step.id} className={conflict ? "bg-red-50" : undefined}>
                    <td>
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => moveStep(step.id, "up")}
                          disabled={i === 0}
                          className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-20 leading-none"
                          title="Move up"
                        >
                          ▲
                        </button>
                        <span className="font-mono text-xs text-center">{step.order_index}</span>
                        <button
                          onClick={() => moveStep(step.id, "down")}
                          disabled={i === steps.length - 1}
                          className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-20 leading-none"
                          title="Move down"
                        >
                          ▼
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="text-sm text-gray-900 font-medium leading-tight">{step.card_title}</div>
                      <div className="text-xs text-gray-400">{step.card_type}</div>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={step.required}
                        onChange={(e) => updateStepLocal(step.id, { required: e.target.checked })}
                        className="h-4 w-4"
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={step.repeatable}
                        onChange={(e) => updateStepLocal(step.id, { repeatable: e.target.checked })}
                        className="h-4 w-4"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        placeholder="–"
                        value={step.cooldown_days ?? ""}
                        onChange={(e) =>
                          updateStepLocal(step.id, {
                            cooldown_days: e.target.value === "" ? null : parseInt(e.target.value),
                          })
                        }
                        disabled={!step.repeatable}
                        className="admin-input w-20 disabled:opacity-40"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        placeholder="Optional override"
                        value={step.step_title_override ?? ""}
                        onChange={(e) =>
                          updateStepLocal(step.id, { step_title_override: e.target.value })
                        }
                        className="admin-input w-full"
                      />
                    </td>
                    <td>
                      {conflict ? (
                        <span className="text-xs text-red-600 font-medium">
                          Required OR repeatable, not both
                        </span>
                      ) : (
                        <button
                          onClick={() => saveStep(step)}
                          disabled={savingStep === step.id}
                          className="rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
                        >
                          {savingStep === step.id ? "Saving…" : "Save"}
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => deleteStep(step.id)}
                        disabled={deletingStep === step.id}
                        className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                      >
                        {deletingStep === step.id ? "…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {steps.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-gray-400 py-6">
                    No steps yet — add one below
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Add step section */}
          {!showAddForm ? (
            <button
              onClick={() => setShowAddForm(true)}
              className="rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-colors"
            >
              + Add step
            </button>
          ) : (
            <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 max-w-xl">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Add step</h3>

              {/* Card picker */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Search content library</label>
                <input
                  type="text"
                  placeholder="Type to search by title…"
                  value={cardSearch}
                  onChange={(e) => searchCards(e.target.value)}
                  className="admin-input w-full"
                />
                {cardSearchLoading && (
                  <p className="text-xs text-gray-400 mt-1">Searching…</p>
                )}
                {cardResults.length > 0 && !selectedCard && (
                  <ul className="mt-1 max-h-48 overflow-y-auto border border-gray-200 rounded bg-white divide-y divide-gray-100">
                    {cardResults.map((c) => (
                      <li key={c.id}>
                        <button
                          onClick={() => {
                            setSelectedCard(c);
                            setCardSearch(c.title || c.body.slice(0, 60));
                            setCardResults([]);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm"
                        >
                          <span className="font-medium text-gray-900">
                            {c.title || c.body.slice(0, 60)}
                          </span>
                          <span className="ml-2 text-xs text-gray-400">{c.type}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {selectedCard && (
                  <div className="mt-1 flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded px-2 py-1">
                    <span>✓ {selectedCard.title || selectedCard.body.slice(0, 60)}</span>
                    <button
                      onClick={() => { setSelectedCard(null); setCardSearch(""); }}
                      className="text-gray-400 hover:text-gray-600 ml-auto"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={addRequired}
                    onChange={(e) => setAddRequired(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Required
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={addRepeatable}
                    onChange={(e) => setAddRepeatable(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Repeatable
                </label>
              </div>

              {addConflict && (
                <p className="text-xs text-red-600 font-medium mb-3">
                  Cards can be required OR repeatable, not both.
                </p>
              )}

              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Cooldown (days) — only applies when repeatable
                </label>
                <input
                  type="number"
                  min={0}
                  placeholder="Leave blank for none"
                  value={addCooldown}
                  onChange={(e) => setAddCooldown(e.target.value)}
                  disabled={!addRepeatable}
                  className="admin-input w-32 disabled:opacity-40"
                />
              </div>

              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Title override (optional)
                </label>
                <input
                  type="text"
                  placeholder="Leave blank to use card title"
                  value={addTitleOverride}
                  onChange={(e) => setAddTitleOverride(e.target.value)}
                  className="admin-input w-full"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={addStep}
                  disabled={!selectedCard || addConflict || addingStep}
                  className="rounded-md bg-orange-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  {addingStep ? "Adding…" : "Add step"}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setSelectedCard(null);
                    setCardSearch("");
                    setCardResults([]);
                    setAddRequired(true);
                    setAddRepeatable(false);
                    setAddCooldown("");
                    setAddTitleOverride("");
                  }}
                  className="rounded-md border border-gray-300 px-4 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
